import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { fillRouteWithSlugs } from "@utils";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "src/components";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { BulkTaskProgramAssignmentForm } from "src/components/Trainings/BulkTaskProgramAssignmentForm";
import { useOpenTrainingQuizAgentPanel } from "src/components/Trainings/useOpenTrainingQuizAgentPanel";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import {
  useGetDevices,
  useGetDocuments,
  useGetUser,
  useGetUsersByOrg,
} from "src/hooks";
import { useGetFunctionalGroups } from "src/hooks/queries";
import { useGetTaskPrograms } from "src/hooks/queries/task-program-mutations";
import type { TaskProgram } from "src/services/backend";
import { EntitlementType } from "src/stores/models";
import { hasFunctionalGroupEntitlement } from "src/utils/entitlements";

export const TrainingAssignment = () => {
  const { orgId = "" } = useParams();
  const navigate = useNavigate();
  const openQuizAgent = useOpenTrainingQuizAgentPanel();
  const { data: user, isLoading: isLoadingUser } = useGetUser();
  const canAdministerAllTrainings =
    !!user?.permissions?.includes("write:configuration") ||
    hasFunctionalGroupEntitlement(
      user,
      orgId,
      EntitlementType.ADMINISTER_ALL_TRAININGS
    );
  const shouldLoadAssignmentData = canAdministerAllTrainings && !!orgId;

  const { data: functionalGroups = [], isLoading: functionalGroupsLoading } =
    useGetFunctionalGroups(orgId, { enabled: shouldLoadAssignmentData });
  const { data: documents = [], isLoading: documentsLoading } = useGetDocuments(
    orgId,
    "",
    {
      includeOrgDocs: true,
      enabled: shouldLoadAssignmentData,
    }
  );
  const { data: devices = [], isLoading: devicesLoading } = useGetDevices({
    orgId,
    enabled: shouldLoadAssignmentData,
  });
  const { data: orgUsers = [], isLoading: usersLoading } = useGetUsersByOrg({
    orgId,
    enabled: shouldLoadAssignmentData,
  });
  const { data: programs = [], isLoading: programsLoading } =
    useGetTaskPrograms(orgId, "all", { enabled: shouldLoadAssignmentData });

  const trainingsHref = fillRouteWithSlugs(ROUTES.TRAININGS, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
  });
  const pageHeader = (
    <PageHeader
      pageTitleComponent={
        <PageBreadcrumb
          items={[
            { label: "Training Programs", to: trainingsHref },
            { label: "Assign" },
          ]}
        />
      }
    />
  );

  const sortedGroups = useMemo(
    () =>
      [...functionalGroups].sort((a, b) =>
        a.acronym.localeCompare(b.acronym, undefined, { sensitivity: "base" })
      ),
    [functionalGroups]
  );

  const assignmentDataLoading =
    functionalGroupsLoading ||
    documentsLoading ||
    devicesLoading ||
    usersLoading ||
    programsLoading;

  const openAssignedQuizzes = (assignedPrograms: TaskProgram[]) => {
    const quizzes = assignedPrograms.flatMap((program) => {
      const documentVersion = program.linkedDocument.versions?.[0];
      if (
        program.completionMethod !== "QUIZ" ||
        documentVersion?.revision == null ||
        program.quizzes?.some(
          (quiz) =>
            quiz.documentVersionId === documentVersion.id && quiz.publishedAt
        )
      ) {
        return [];
      }
      return [
        {
          programId: program.id,
          documentId: program.linkedDocument.id,
          documentVersionId: documentVersion.id,
          revision: documentVersion.revision,
        },
      ];
    });

    if (quizzes.length > 0) openQuizAgent(quizzes);
  };

  if (isLoadingUser || (canAdministerAllTrainings && assignmentDataLoading)) {
    return (
      <Box className="flex flex-col gap-4">
        {pageHeader}
        <Box className="flex justify-center py-8">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!canAdministerAllTrainings) {
    return (
      <Box className="flex flex-col gap-4">
        {pageHeader}
        <Box className="flex flex-1 flex-col items-center justify-center gap-2 px-3 py-8">
          <Typography color="error">
            You do not have permission to assign trainings.
          </Typography>
          <Button onClick={() => navigate(trainingsHref)}>
            Back to Trainings
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="flex flex-col gap-4">
      {pageHeader}
      <Box className="mx-auto w-full max-w-[800px] px-3 py-2">
        <Typography variant="h5" sx={{ mb: 2 }}>
          Assign document trainings
        </Typography>
        <BulkTaskProgramAssignmentForm
          orgId={orgId}
          functionalGroups={sortedGroups}
          documents={documents}
          documentsLoading={documentsLoading}
          devices={devices}
          devicesLoading={devicesLoading}
          orgUsers={orgUsers}
          usersLoading={usersLoading}
          programs={programs}
          onQuizProgramsAssigned={openAssignedQuizzes}
          onCancel={() => navigate(trainingsHref)}
          onSubmitted={() => navigate(trainingsHref)}
        />
      </Box>
    </Box>
  );
};
